import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllUsers,
  editUserAdmin,
  deleteUser,
} from "../../../services/operations/authAPI";
import { useTranslation } from "react-i18next";
import { 
  UserPlus, 
  Search, 
  Pencil, 
  Trash2,
  ChevronDown,
  ChevronUp,
  History,
  Activity,
  CreditCard
} from "lucide-react";
import { getSchoolBySchoolID, getSchools } from "../../../services/operations/schoolAPI";

const accessStyles = {
  school_admin: "bg-green-100 text-green-600",
  student: "bg-blue-100 text-blue-600",
  parent: "bg-purple-100 text-purple-600",
  teacher: "bg-orange-100 text-orange-600",
};

// Light mode adapted styles for subscription plans
const planStyles = {
  Premium: "bg-purple-100 text-purple-700 border border-purple-200",
  Basic: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  Free: "bg-gray-100 text-gray-700 border border-gray-200",
  None: "bg-gray-100 text-gray-500 border border-gray-200",
};

export default function UserManagement() {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.profile);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    _id: "",
    name: "",
    email: "",
    phoneno: "",
    role: "",
    schoolId: "",
    studentClass: "",
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolNames, setSchoolNames] = useState({});
  
  // Expanded row state
  const [expandedUserId, setExpandedUserId] = useState(null);

  const fetchUsers = async () => {
    const fetchedUsers = await dispatch(getAllUsers());
    setUsers(fetchedUsers || []);
  };

  const getSchoolName = (schoolId) => {
    if (!schoolId) return "No School";
    if (schoolNames[schoolId]) return schoolNames[schoolId];

    getSchoolBySchoolID(schoolId).then((res) => {
      const name = res?.name || "Unknown School";
      setSchoolNames((prev) => ({
        ...prev,
        [schoolId]: name,
      }));
    });
    return "Loading...";
  };

  useEffect(() => {
    const loadSchools = async () => {
      const list = await getSchools();
      if (list) setSchools(list);
    };
    loadSchools();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditUser = (e, userObj) => {
    e.stopPropagation(); // Prevent row expansion
    setEditUserData({
      _id: userObj._id,
      name: userObj.name,
      email: userObj.email,
      phoneno: userObj.phoneno || "",
      role: userObj.role,
      schoolId: userObj.schoolId || "",
      studentClass: userObj.class || "",
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (e, userId) => {
    e.stopPropagation(); // Prevent row expansion
    setSelectedUserId(userId);
    setIsDeleteModalOpen(true);
  };

  const handleSaveUser = () => {
    const payload = {
      name: editUserData.name,
      email: editUserData.email,
      phoneno: editUserData.phoneno,
      role: editUserData.role,
      schoolId: editUserData.schoolId,
      studentClass: editUserData.studentClass,
    };
    dispatch(editUserAdmin(editUserData._id, payload, fetchUsers));
    setIsEditModalOpen(false);
  };

  const toggleExpand = (userId) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  const getActivePlan = (subscriptions) => {
    if (!subscriptions || !Array.isArray(subscriptions)) return null;
    const activeSub = subscriptions.find((sub) => sub.status === "active");
    return activeSub ? activeSub.plan : null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Reusable component for subscription history (Light Mode Adapted)
  const SubscriptionHistoryPanel = ({ subscriptions }) => {
    if (!subscriptions || subscriptions.length === 0) {
      return (
        <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
          No subscription history found for this user.
        </div>
      );
    }

    const sortedSubs = [...subscriptions].sort(
      (a, b) =>
        new Date(b.createdAt || b.planActivatedDate) -
        new Date(a.createdAt || a.planActivatedDate)
    );

    return (
      <div className="p-5 bg-gray-50 rounded-xl border border-gray-200 shadow-inner">
        <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <History className="w-4 h-4 text-blue-600" />
          Subscription History
        </h4>

        <div className="max-h-[280px] overflow-y-auto pr-2 -mr-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-2">
            {sortedSubs.map((sub, idx) => {
              const planName = sub.plan
                ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1)
                : "Unknown";
              const isActive = sub.status === "active";
              const apisUsed = sub.planDetails?.apisUsed || 0;
              const maxApis = sub.planDetails?.maxAPIsAllowed || 0;
              const isUnlimited = maxApis === -1;

              return (
                <div
                  key={sub._id || idx}
                  className={`p-4 rounded-xl border transition-colors ${
                    isActive
                      ? "border-blue-300 bg-blue-50"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold ${
                          planStyles[planName] || planStyles.None
                        }`}
                      >
                        {planName}
                      </span>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider ${
                          isActive ? "text-green-600" : "text-gray-500"
                        }`}
                      >
                        • {sub.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Activated:</span>
                      <span className="text-gray-800 font-medium">
                        {formatDate(sub.planActivatedDate)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Expires:</span>
                      <span className="text-gray-800 font-medium">
                        {formatDate(sub.planExpireDate)}
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-500 flex items-center gap-1.5">
                        <Activity className="w-3.5 h-3.5" /> API Usage
                      </span>
                      <span className="text-xs font-semibold text-gray-800">
                        {apisUsed} / {isUnlimited ? "∞" : maxApis}
                      </span>
                    </div>
                    {!isUnlimited && (
                      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            isActive ? "bg-blue-500" : "bg-gray-400"
                          }`}
                          style={{
                            width: `${Math.min((apisUsed / (maxApis || 1)) * 100, 100)}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-6 pt-4 pb-6">
      <h2 className="text-lg sm:text-xl font-bold text-blue-900">
        {t("userManagement.title")}
      </h2>
      <p className="text-sm sm:text-md text-slate-500 mb-6">
        {t("userManagement.subtitle")}
      </p>

      {/* Search and Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3 sm:gap-0">
        <div className="font-semibold text-sm sm:text-base">
          {t("userManagement.allUsers")}{" "}
          <span className="text-slate-500">{users.length}</span>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              placeholder={t("userManagement.searchHere")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 text-sm shadow-sm"
            />
            <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
          </div>

          <button
            onClick={() => navigate("/dashboard/super-admin/users/add-user")}
            className="bg-[#00254D] text-white px-4 py-2 rounded-md text-sm flex flex-row sm:flex-row items-center justify-center gap-3 sm:gap-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>{t("userManagement.addUser")}</span>
          </button>
        </div>
      </div>

      {/* --- ✅ Mobile Card View --- */}
      <div className="block md:hidden space-y-4 mt-4">
        {users
          .filter((u) => {
            const query = search.toLowerCase();
            return (
              u.name.toLowerCase().includes(query) ||
              u.role?.toLowerCase().includes(query) ||
              u.email.toLowerCase().includes(query)
            );
          })
          .map((userObj) => {
            const activePlanName = getActivePlan(userObj.subscription);
            const displayPlan = activePlanName
              ? activePlanName.charAt(0).toUpperCase() + activePlanName.slice(1)
              : "None";
            const isExpanded = expandedUserId === userObj._id;

            return (
              <div
                key={userObj._id}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        {userObj.image ? (
                          <img
                            src={userObj.image}
                            alt="User"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-gray-700">
                            {userObj.name[0].toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900">{userObj.name}</p>
                        <p className="text-sm text-gray-500">{userObj.email}</p>
                        <p className="text-xs text-gray-500 mt-1">{getSchoolName(userObj.schoolId)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        accessStyles[userObj.role] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {userObj.role}
                    </span>
                    <span
                      className={`px-2.5 py-1 flex items-center gap-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
                        planStyles[displayPlan] || planStyles.None
                      }`}
                    >
                      <CreditCard size={12} />
                      {displayPlan}
                    </span>
                  </div>

                  <div className="text-xs text-gray-500 flex justify-between">
                    <span>{t("userManagement.lastActive")}: {new Date(userObj.lastActive).toLocaleDateString()}</span>
                    <span>{t("userManagement.dateAdded")}: {new Date(userObj.createdAt).toLocaleDateString()}</span>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 gap-2">
                    <button
                      onClick={() => toggleExpand(userObj._id)}
                      className="text-xs font-medium text-blue-600 flex items-center gap-1 px-2 py-2 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {isExpanded ? "Hide Subscriptions" : "View Subscriptions"}
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <div className="flex gap-3">
                      <Pencil
                        className="w-4 h-4 text-gray-500 hover:text-blue-600 cursor-pointer"
                        onClick={(e) => handleEditUser(e, userObj)}
                      />
                      <Trash2
                        className="w-4 h-4 text-red-500 hover:text-red-700 cursor-pointer"
                        onClick={(e) => handleDeleteUser(e, userObj._id)}
                      />
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <SubscriptionHistoryPanel subscriptions={userObj.subscription} />
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* --- ✅ Desktop Table View --- */}
      <div className="hidden md:block border rounded-md overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-left text-gray-600 border-b">
            <tr>
              <th className="p-3 w-10"></th>
              <th className="p-3">
                <input type="checkbox" />
              </th>
              <th className="p-3">{t("userManagement.userName")}</th>
              <th className="p-3">{t("userManagement.school")}</th>
              <th className="p-3">{t("userManagement.access")}</th>
              <th className="p-3">Active Plan</th>
              <th className="p-3">{t("userManagement.lastActive")} ↓</th>
              <th className="p-3">{t("userManagement.dateAdded")}</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((u) => {
                const query = search.toLowerCase();
                return (
                  u.name.toLowerCase().includes(query) ||
                  u.role?.toLowerCase().includes(query) ||
                  u.email.toLowerCase().includes(query)
                );
              })
              .map((userObj) => {
                const activePlanName = getActivePlan(userObj.subscription);
                const displayPlan = activePlanName
                  ? activePlanName.charAt(0).toUpperCase() + activePlanName.slice(1)
                  : "None";
                const isExpanded = expandedUserId === userObj._id;

                return (
                  <React.Fragment key={userObj._id}>
                    <tr 
                      onClick={() => toggleExpand(userObj._id)}
                      className={`border-t text-md cursor-pointer transition-colors ${
                        isExpanded ? "bg-gray-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="p-3 text-gray-500">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" />
                      </td>
                      <td className="p-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          {userObj.image ? (
                            <img
                              src={userObj.image}
                              alt="User"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs font-medium text-gray-700">
                              {userObj.name[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{userObj.name}</p>
                          <p className="text-xs text-gray-500">{userObj.email}</p>
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        {getSchoolName(userObj.schoolId)}
                      </td>
                      <td className="p-3 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            accessStyles[userObj.role] || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {userObj.role}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        <span
                          className={`px-2.5 py-1 flex items-center gap-1 w-max rounded-full text-[11px] uppercase tracking-wider font-semibold ${
                            planStyles[displayPlan] || planStyles.None
                          }`}
                        >
                          <CreditCard size={12} />
                          {displayPlan}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(userObj.lastActive).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(userObj.createdAt).toLocaleDateString()}
                      </td>
                      <td className="p-3 flex justify-end gap-3" onClick={(e) => e.stopPropagation()}>
                        <Pencil
                          className="w-4 h-4 text-gray-500 hover:text-blue-600 cursor-pointer"
                          onClick={(e) => handleEditUser(e, userObj)}
                        />
                        <Trash2
                          className="w-4 h-4 text-red-500 hover:text-red-700 cursor-pointer"
                          onClick={(e) => handleDeleteUser(e, userObj._id)}
                        />
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <td colSpan={9} className="p-6 pt-2 pb-6 shadow-inner">
                          <SubscriptionHistoryPanel subscriptions={userObj.subscription} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* === ✅ Edit User Modal === */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-blue-900">
              {t("userManagement.updateUser")}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("userManagement.name")}
                </label>
                <input
                  type="text"
                  value={editUserData.name}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, name: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("userManagement.email")}
                </label>
                <input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, email: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                {t("userManagement.phoneNumber")}
              </label>
              <input
                type="text"
                value={editUserData.phoneno}
                onChange={(e) =>
                  setEditUserData({ ...editUserData, phoneno: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Enter phone number"
              />
            </div>

            {editUserData.role && editUserData.role !== "super_admin" && (
              <div>
                <label className="text-sm font-semibold text-gray-700 mt-2 block">
                  Select School
                </label>

                <select
                  name="schoolId"
                  value={editUserData.schoolId}
                  onChange={(e) =>
                    setEditUserData({
                      ...editUserData,
                      schoolId: e.target.value,
                    })
                  }
                  className="mt-1 w-full px-4 py-2 border rounded-md bg-white"
                >
                  <option value="" disabled>
                    -- Select School --
                  </option>
                  {schools.map((school) => (
                    <option key={school._id} value={school._id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {user?.role === "super_admin" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                  {t("userManagement.role")}
                </label>
                <select
                  value={editUserData.role}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, role: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="student">{t("userManagement.student")}</option>
                  <option value="teacher">{t("userManagement.teacher")}</option>
                  <option value="parent">{t("userManagement.parent")}</option>
                  <option value="school_admin">
                    {t("userManagement.admin")}
                  </option>
                  <option value="super_admin">
                    {t("userManagement.superadmin")}
                  </option>
                </select>
              </div>
            )}

            {/* ✅ ADDED: Class Dropdown (Only for Students) */}
            {editUserData.role === "student" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                  {t("userManagement.class") || "Class"}
                </label>
                <select
                  value={editUserData.studentClass}
                  onChange={(e) =>
                    setEditUserData({ ...editUserData, studentClass: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="" disabled>-- Select Class --</option>
                  <option value="6">Class 6</option>
                  <option value="7">Class 7</option>
                  <option value="8">Class 8</option>
                  <option value="9">Class 9</option>
                  <option value="10">Class 10</option>
                </select>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded-md text-sm"
              >
                {t("userManagement.cancel")}
              </button>
              <button
                onClick={handleSaveUser}
                className="px-4 py-2 bg-[#00254D] text-white rounded-md text-sm"
              >
                {t("userManagement.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === ✅ Delete Confirmation Modal === */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-[#00254D]">
              {t("userManagement.deleteUser")}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t("userManagement.deleteConfirmation")}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded-md text-sm "
              >
                {t("userManagement.cancel")}
              </button>
              <button
                onClick={() => {
                  dispatch(deleteUser(selectedUserId, fetchUsers));
                  setIsDeleteModalOpen(false);
                }}
                className="px-4 py-2 bg-[#00254D] hover:bg-red-600 text-white rounded-md text-sm"
              >
                {t("userManagement.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}